import { ThunkDispatch } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { deletePhase, fetchPhases, fromPhases, savePhase, updatePhase } from './phases';
import { Phase } from './phaseTypes';

/**
 * Hook to fetch and manage phases for a specific event
 *
 * @param eventId - The ID of the event to fetch phases for
 * @returns {Object} An object containing:
 *   - phases: Array of Phase objects for the event
 *   - isLoading: Boolean indicating if phases are currently being fetched
 *
 * @example
 * const { phases, isLoading } = useFetchPhases('123');
 * console.log(phases);
 * if (isLoading) console.log('Loading phases...');
 */
export const useFetchPhases = (eventId: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const phases = useSelector((state: Map<string, any>) =>
    fromPhases.getPhasesByEventId(state?.toJS(), eventId),
  );
  const isLoading = useSelector((state: Map<string, any>) =>
    fromPhases.getIsLoading(state?.toJS()),
  );

  useEffect(() => {
    if (!eventId) return;

    dispatch(fetchPhases(eventId));
  }, [eventId, dispatch]);

  return {
    phases,
    isLoading,
  };
};

/**
 * Hook to delete a phase and track deletion state.
 *
 * @returns {Object} An object containing:
 *   - deletePhase: Function to dispatch the delete action. Call with (eventId, phaseId).
 *   - deleting: Boolean indicating if a phase is currently being deleted.
 *
 * @example
 * const { deletePhase, deleting } = useDeletePhase();
 * deletePhase(eventId, phaseId);
 * if (deleting) { ... }
 */
export const useDeletePhase = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const deleting = useSelector((state: Map<string, any>) => fromPhases.getIsDeleting(state.toJS()));

  const deletePhaseFn = (eventId: string, phaseId: string) =>
    dispatch(deletePhase({ eventId, phaseId }));

  return { deletePhase: deletePhaseFn, deleting };
};

/**
 * Hook to create a phase and track saving state.
 *
 * @returns {Object} An object containing:
 *   - savePhase: Function to dispatch the save action. Call with (eventId, phase).
 *   - saving: Boolean indicating if a phase is currently being saved.
 *
 * @example
 * const { savePhase, saving } = useSavePhase();
 * savePhase(eventId, phase);
 * if (saving) { ... }
 */
export const useSavePhase = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromPhases.getIsSaving(state.toJS()));

  const savePhaseFn = (eventId: string, phase: Phase) => dispatch(savePhase({ eventId, phase }));

  return { savePhase: savePhaseFn, saving };
};

/**
 * Hook to update a phase and track saving state.
 *
 * @returns {Object} An object containing:
 *   - updatePhase: Function to dispatch the update action. Call with (eventId, phaseId, phase).
 *   - saving: Boolean indicating if a phase is currently being saved.
 *
 * @example
 * const { updatePhase, saving } = useUpdatePhase();
 * updatePhase(eventId, phaseId, phase);
 * if (saving) { ... }
 */
export const useUpdatePhase = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromPhases.getIsSaving(state.toJS()));

  const updatePhaseFn = (phaseId: string, phase: Phase) =>
    dispatch(updatePhase({ phaseId, phase }));

  return { updatePhase: updatePhaseFn, saving };
};
